import { useState } from 'react';
import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import type {
  CalculatedFieldSpec,
  CalculatedFieldType,
  CaseBucketConfig,
  DateDiffConfig,
  RatioConfig,
  BucketDef,
} from '../../types/reportBuilder';
import { X, Plus } from '@phosphor-icons/react';

const FIELD_TYPES: { value: CalculatedFieldType; label: string; description: string }[] = [
  { value: 'case_bucket', label: 'Bucket (CASE)', description: 'Group values into labeled ranges' },
  { value: 'date_diff', label: 'Date Difference', description: 'Calculate time between two dates' },
  { value: 'ratio', label: 'Ratio', description: 'Divide one measure by another' },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function CalculatedFieldBuilder({ open, onClose }: Props) {
  const { getDimensionColumns, getDateColumns, getMeasureColumns, addCalculatedField } = useReportBuilderStore();

  const [fieldType, setFieldType] = useState<CalculatedFieldType>('case_bucket');
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [asDimension, setAsDimension] = useState(true);

  // Case bucket state
  const [sourceColumn, setSourceColumn] = useState('');
  const [buckets, setBuckets] = useState<BucketDef[]>([{ label: '', min_value: undefined, max_value: undefined }]);
  const [elseLabel, setElseLabel] = useState('Other');

  // Date diff state
  const [startColumn, setStartColumn] = useState('');
  const [endColumn, setEndColumn] = useState('__now__');
  const [unit, setUnit] = useState<'year' | 'month' | 'day'>('year');

  // Ratio state
  const [numColumn, setNumColumn] = useState('');
  const [numAgg, setNumAgg] = useState<string>('count');
  const [denColumn, setDenColumn] = useState('');
  const [denAgg, setDenAgg] = useState<string>('count');

  if (!open) return null;

  const dimensionCols = getDimensionColumns();
  const dateCols = getDateColumns();
  const measureCols = getMeasureColumns();
  const allCols = [...dimensionCols, ...measureCols];

  const handleAddBucket = () => {
    setBuckets([...buckets, { label: '', min_value: undefined, max_value: undefined }]);
  };

  const handleRemoveBucket = (idx: number) => {
    setBuckets(buckets.filter((_, i) => i !== idx));
  };

  const handleUpdateBucket = (idx: number, updates: Partial<BucketDef>) => {
    setBuckets(buckets.map((b, i) => (i === idx ? { ...b, ...updates } : b)));
  };

  const handleSave = () => {
    if (!key || !label) return;

    let config: CaseBucketConfig | DateDiffConfig | RatioConfig;
    let isDim = asDimension;

    switch (fieldType) {
      case 'case_bucket':
        config = { source_column: sourceColumn, buckets, else_label: elseLabel } as CaseBucketConfig;
        isDim = true;
        break;
      case 'date_diff':
        config = { start_column: startColumn, end_column: endColumn, unit } as DateDiffConfig;
        isDim = true;
        break;
      case 'ratio':
        config = {
          numerator: { column: numColumn, aggregation: numAgg, alias: `${numAgg}_${numColumn}` },
          denominator: { column: denColumn, aggregation: denAgg, alias: `${denAgg}_${denColumn}` },
        } as RatioConfig;
        isDim = false;
        break;
    }

    const field: CalculatedFieldSpec = {
      key,
      label,
      field_type: fieldType,
      config,
      as_dimension: isDim,
    };

    addCalculatedField(field);
    onClose();
  };

  const inputClass = 'w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none';
  const labelClass = 'block text-[10px] font-medium text-gray-500 mb-0.5';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="w-[480px] max-h-[80vh] overflow-y-auto rounded-lg border bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold">Add Calculated Field</h3>
          <button onClick={onClose} className="rounded p-1 hover:bg-gray-100"><X size={14} /></button>
        </div>

        <div className="space-y-3 p-4">
          {/* Key & Label */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClass}>Key (unique ID)</label>
              <input value={key} onChange={(e) => setKey(e.target.value)} className={inputClass} placeholder="e.g. tenure_band" />
            </div>
            <div>
              <label className={labelClass}>Display Label</label>
              <input value={label} onChange={(e) => setLabel(e.target.value)} className={inputClass} placeholder="e.g. Tenure Band" />
            </div>
          </div>

          {/* Type selector */}
          <div>
            <label className={labelClass}>Calculation Type</label>
            <div className="grid grid-cols-3 gap-1">
              {FIELD_TYPES.map((ft) => (
                <button key={ft.value} onClick={() => setFieldType(ft.value)}
                  className={`rounded border px-2 py-1.5 text-xs text-left ${fieldType === ft.value ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <div className="font-medium">{ft.label}</div>
                  <div className="text-[9px] text-gray-400 mt-0.5">{ft.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Case Bucket config */}
          {fieldType === 'case_bucket' && (() => {
            const srcCol = allCols.find(c => c.refKey === sourceColumn);
            const isNumeric = srcCol?.data_type === 'integer' || srcCol?.data_type === 'decimal';
            return (
            <div className="space-y-2">
              <div>
                <label className={labelClass}>Source Column</label>
                <select value={sourceColumn} onChange={(e) => {
                  setSourceColumn(e.target.value);
                  // Reset buckets when column changes
                  setBuckets([{ label: '', min_value: undefined, max_value: undefined, equals: undefined }]);
                }} className={inputClass}>
                  <option value="">Select column...</option>
                  {allCols.map((c) => <option key={c.refKey} value={c.refKey}>{c.label} ({c.data_type})</option>)}
                </select>
              </div>
              {sourceColumn && (
                <div className="rounded bg-gray-50 px-2 py-1 text-[10px] text-gray-500">
                  {isNumeric
                    ? 'Numeric column — define value ranges (min/max) for each bucket.'
                    : 'Text column — define exact match values for each bucket.'}
                </div>
              )}
              <div>
                <label className={labelClass}>Buckets</label>
                {buckets.map((b, idx) => (
                  <div key={idx} className="flex gap-1 items-center mb-1">
                    <input value={b.label} onChange={(e) => handleUpdateBucket(idx, { label: e.target.value })}
                      className={inputClass} placeholder="Label" style={{ width: '30%' }} />
                    {isNumeric ? (
                      <>
                        <input type="number" value={b.min_value ?? ''} onChange={(e) => handleUpdateBucket(idx, { min_value: e.target.value ? Number(e.target.value) : undefined, equals: undefined })}
                          className={inputClass} placeholder="Min (>=)" style={{ width: '25%' }} />
                        <input type="number" value={b.max_value ?? ''} onChange={(e) => handleUpdateBucket(idx, { max_value: e.target.value ? Number(e.target.value) : undefined, equals: undefined })}
                          className={inputClass} placeholder="Max (<)" style={{ width: '25%' }} />
                      </>
                    ) : (
                      <input value={b.equals ?? ''} onChange={(e) => handleUpdateBucket(idx, { equals: e.target.value || undefined, min_value: undefined, max_value: undefined })}
                        className={inputClass} placeholder="Exact value" style={{ width: '50%' }} />
                    )}
                    <button onClick={() => handleRemoveBucket(idx)} className="rounded p-0.5 hover:bg-gray-100"><X size={10} /></button>
                  </div>
                ))}
                <button onClick={handleAddBucket} className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-700">
                  <Plus size={10} /> Add bucket
                </button>
              </div>
              <div>
                <label className={labelClass}>Else Label</label>
                <input value={elseLabel} onChange={(e) => setElseLabel(e.target.value)} className={inputClass} />
              </div>
            </div>
            );
          })()}

          {/* Date Diff config */}
          {fieldType === 'date_diff' && (
            <div className="space-y-2">
              <div>
                <label className={labelClass}>Start Date Column</label>
                <select value={startColumn} onChange={(e) => setStartColumn(e.target.value)} className={inputClass}>
                  <option value="">Select column...</option>
                  {dateCols.map((c) => <option key={c.refKey} value={c.refKey}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>End Date</label>
                <select value={endColumn} onChange={(e) => setEndColumn(e.target.value)} className={inputClass}>
                  <option value="__now__">Current Date (now)</option>
                  {dateCols.map((c) => <option key={c.refKey} value={c.refKey}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Unit</label>
                <select value={unit} onChange={(e) => setUnit(e.target.value as 'year' | 'month' | 'day')} className={inputClass}>
                  <option value="year">Years</option>
                  <option value="month">Months</option>
                  <option value="day">Days</option>
                </select>
              </div>
            </div>
          )}

          {/* Ratio config */}
          {fieldType === 'ratio' && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelClass}>Numerator Column</label>
                  <select value={numColumn} onChange={(e) => setNumColumn(e.target.value)} className={inputClass}>
                    <option value="">Select column...</option>
                    {allCols.map((c) => <option key={c.refKey} value={c.refKey}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Numerator Aggregation</label>
                  <select value={numAgg} onChange={(e) => setNumAgg(e.target.value)} className={inputClass}>
                    <option value="count">COUNT</option>
                    <option value="count_distinct">COUNT DISTINCT</option>
                    <option value="sum">SUM</option>
                    <option value="avg">AVG</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelClass}>Denominator Column</label>
                  <select value={denColumn} onChange={(e) => setDenColumn(e.target.value)} className={inputClass}>
                    <option value="">Select column...</option>
                    {allCols.map((c) => <option key={c.refKey} value={c.refKey}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Denominator Aggregation</label>
                  <select value={denAgg} onChange={(e) => setDenAgg(e.target.value)} className={inputClass}>
                    <option value="count">COUNT</option>
                    <option value="count_distinct">COUNT DISTINCT</option>
                    <option value="sum">SUM</option>
                    <option value="avg">AVG</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <button onClick={onClose} className="rounded border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={!key || !label}
            className="rounded bg-primary px-3 py-1.5 text-xs text-white hover:bg-primary/90 disabled:opacity-50">
            Add Field
          </button>
        </div>
      </div>
    </div>
  );
}
